import {
  SkeletonCard,
  SkeletonChart,
  SkeletonFilterBar,
  SkeletonFormField,
  SkeletonPageHeader,
  SkeletonStatCard,
  SkeletonTable,
  SkeletonText,
} from "./skeleton-blocks";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Full-page skeleton layouts, composed from skeleton-blocks.
 * Each corresponds to a real page shape in the admin area.
 */

export function DashboardPageSkeleton() {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader />

      {/* Stat card grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      {/* Chart + side panel */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SkeletonChart />
        </div>
        <SkeletonCard className="space-y-4">
          <Skeleton className="h-5 w-32" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </SkeletonCard>
      </div>
    </div>
  );
}

export function TableListPageSkeleton({
  rows = 8,
  columns = 5,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader />
      <SkeletonFilterBar />
      <SkeletonTable rows={rows} columns={columns} />
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
          <Skeleton className="h-9 w-9 rounded-md" />
        </div>
      </div>
    </div>
  );
}

export function DetailPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Skeleton className="h-4 w-64" />

      {/* Hero card */}
      <SkeletonCard className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <Skeleton className="h-14 w-14 rounded-xl" />
            <div className="space-y-2">
              <Skeleton className="h-7 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-28" />
            <Skeleton className="h-10 w-28" />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      </SkeletonCard>

      {/* Tab strip */}
      <div className="flex gap-1 border-b border-outline-variant/10">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-28" />
        ))}
      </div>

      {/* Two-column content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <SkeletonCard className="space-y-4">
            <Skeleton className="h-5 w-40" />
            <SkeletonText lines={4} widths={["100%", "95%", "92%", "70%"]} />
          </SkeletonCard>
          <SkeletonCard className="space-y-4">
            <Skeleton className="h-5 w-48" />
            <SkeletonText lines={3} widths={["100%", "88%", "65%"]} />
          </SkeletonCard>
        </div>
        <SkeletonCard className="space-y-4">
          <Skeleton className="h-5 w-32" />
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-full" />
            </div>
          ))}
        </SkeletonCard>
      </div>
    </div>
  );
}

export function FormPageSkeleton() {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader withActions={false} />

      {/* Stepper */}
      <SkeletonCard className="p-4">
        <div className="flex items-center justify-between gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-1 items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </SkeletonCard>

      {/* Form body */}
      <SkeletonCard className="space-y-6">
        <div>
          <Skeleton className="mb-2 h-6 w-56" />
          <Skeleton className="h-4 w-80" />
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SkeletonFormField />
          <SkeletonFormField />
          <SkeletonFormField className="sm:col-span-2" />
          <SkeletonFormField />
          <SkeletonFormField />
        </div>
        <div className="flex justify-between pt-4">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-32" />
        </div>
      </SkeletonCard>
    </div>
  );
}

export function PublicPageSkeleton() {
  return (
    <div className="space-y-8">
      {/* Hero */}
      <SkeletonCard className="space-y-4 py-10">
        <Skeleton className="mx-auto h-10 w-3/4 max-w-2xl" />
        <Skeleton className="mx-auto h-5 w-1/2 max-w-xl" />
        <div className="mx-auto flex max-w-md justify-center gap-3 pt-4">
          <Skeleton className="h-11 w-32 rounded-full" />
          <Skeleton className="h-11 w-32 rounded-full" />
        </div>
      </SkeletonCard>

      {/* Content grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} className="space-y-3">
            <Skeleton className="h-12 w-12 rounded-xl" />
            <Skeleton className="h-5 w-3/4" />
            <SkeletonText lines={2} widths={["100%", "80%"]} />
            <Skeleton className="h-4 w-24" />
          </SkeletonCard>
        ))}
      </div>
    </div>
  );
}

export function ReportsPageSkeleton() {
  return (
    <div className="space-y-6">
      <SkeletonPageHeader />

      {/* KPI grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      <SkeletonFilterBar fields={4} />

      {/* Primary chart */}
      <SkeletonChart />

      {/* Secondary charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SkeletonChart />
        <SkeletonChart />
      </div>
    </div>
  );
}
